import { Controller } from "@hotwired/stimulus"

// Drives a navbar dropdown (trigger + flyout panel) with a "safe triangle" hover
// bridge. Hovering the trigger opens the panel, but the panel sits a few pixels
// below the trigger — closing on the first mouseleave makes that gap (and the
// diagonal travel toward the first item) almost impossible to cross: the menu
// collapses mid-aim. Instead, when the pointer leaves we keep the menu open as
// long as it stays inside the triangle formed by its exit point and the panel's
// two near corners — i.e. while it is plausibly *aiming* at the panel. This is
// the technique Amazon's mega-menu popularized and Floating UI ships as
// `safePolygon`.
//
// Visibility is JS-driven via a `data-open` attribute (CSS reveals the panel on
// `group-data-[open]` and on `group-focus-within`, so keyboard/AT users get it
// for free). Click toggles, Escape closes, aria-expanded stays synced — the menu
// is never hover-only.
export default class extends Controller {
  static targets = ["trigger", "panel"]

  // How long the pointer may stall inside the safe triangle before we give up
  // and close. Active travel toward the panel refreshes this, so it only bites
  // when the pointer stops in the dead zone and never arrives.
  static values = { closeDelay: { type: Number, default: 250 } }

  connect() {
    this.onFocusOut = this.onFocusOut.bind(this)
    this.onPointerMove = this.onPointerMove.bind(this)
    this.onPointerEnter = this.onPointerEnter.bind(this)
    this.onPointerLeave = this.onPointerLeave.bind(this)

    this.element.addEventListener("focusout", this.onFocusOut)
    this.element.addEventListener("pointerenter", this.onPointerEnter)
    this.element.addEventListener("pointerleave", this.onPointerLeave)
  }

  disconnect() {
    this.element.removeEventListener("focusout", this.onFocusOut)
    this.element.removeEventListener("pointerenter", this.onPointerEnter)
    this.element.removeEventListener("pointerleave", this.onPointerLeave)
    this.stopTracking()
  }

  toggle() {
    this.expanded ? this.close() : this.open()
  }

  open() {
    this.stopTracking()
    this.element.setAttribute("data-open", "true")
    this.triggerTarget.setAttribute("aria-expanded", "true")
  }

  close() {
    this.stopTracking()
    this.element.removeAttribute("data-open")
    this.triggerTarget.setAttribute("aria-expanded", "false")
  }

  onKeydown(event) {
    if (event.key === "Escape") {
      this.close()
      this.triggerTarget.focus()
    }
  }

  // Closing once focus leaves the whole menu keeps aria-expanded honest when a
  // keyboard user tabs out of the flyout.
  onFocusOut(event) {
    if (!this.element.contains(event.relatedTarget)) this.close()
  }

  // Hovering opens — but only a real mouse hovers. A touch tap also fires
  // pointerenter (pointerType "touch"); honouring it would open-then-toggle-shut
  // in one gesture, so touch is left to the click toggle. Gating per event
  // (not on a device-wide `(hover: hover)` media query) also means headless
  // Chrome, which reports `hover: none`, still drives this from synthetic mouse
  // input in tests.
  onPointerEnter(event) {
    if (event.pointerType === "touch") return
    this.open()
  }

  // The pointer left the trigger/panel subtree. Don't close yet — watch where it
  // goes and only close once it leaves the safe triangle or stalls there.
  onPointerLeave(event) {
    if (event.pointerType === "touch") return
    if (!this.expanded) return

    this.safeTriangle = this.buildSafeTriangle(event.clientX, event.clientY)
    if (!this.safeTriangle) {
      this.close()
      return
    }

    document.addEventListener("pointermove", this.onPointerMove)
    this.armStallTimer()
  }

  onPointerMove(event) {
    if (this.element.contains(event.target)) {
      // Back over the trigger or panel — pointerenter keeps it open; stop bridging.
      this.stopTracking()
      return
    }

    if (this.pointInTriangle(event.clientX, event.clientY, this.safeTriangle)) {
      this.armStallTimer()
      return
    }

    this.close()
  }

  armStallTimer() {
    window.clearTimeout(this.stallTimer)
    this.stallTimer = window.setTimeout(() => this.close(), this.closeDelayValue)
  }

  stopTracking() {
    document.removeEventListener("pointermove", this.onPointerMove)
    window.clearTimeout(this.stallTimer)
    this.safeTriangle = null
  }

  // Triangle from the pointer's exit point (apex) to the panel's near edge
  // (base), so the cone is narrow at the cursor and widens to the whole panel —
  // exactly the area the pointer would sweep while aiming at an item. The base is
  // widened sideways and nudged a little *into* the panel so grazing the boundary
  // is forgiven. Our layout always drops the panel below the trigger, but we
  // derive the near edge from geometry so it stays correct if that ever flips.
  buildSafeTriangle(x, y) {
    const rect = this.panelTarget.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return null

    const pad = 12
    const panelBelow = y <= rect.top
    const edgeY = panelBelow ? rect.top + pad : rect.bottom - pad
    return [
      [x, y],
      [rect.left - pad, edgeY],
      [rect.right + pad, edgeY],
    ]
  }

  pointInTriangle(px, py, [[ax, ay], [bx, by], [cx, cy]]) {
    const sign = (x1, y1, x2, y2, x3, y3) => (x1 - x3) * (y2 - y3) - (x2 - x3) * (y1 - y3)
    const d1 = sign(px, py, ax, ay, bx, by)
    const d2 = sign(px, py, bx, by, cx, cy)
    const d3 = sign(px, py, cx, cy, ax, ay)
    const hasNeg = d1 < 0 || d2 < 0 || d3 < 0
    const hasPos = d1 > 0 || d2 > 0 || d3 > 0
    return !(hasNeg && hasPos)
  }

  get expanded() {
    return this.triggerTarget.getAttribute("aria-expanded") === "true"
  }
}
